// src/lib/api/financial/subcontractors.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'

/**
 * List all subcontractors for the company
 */
const _listSubcontractors = async () => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('subcontractors')
    .select('*')
    .eq('company_id', companyId)
    .order('name', { ascending: true })

  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create a new subcontractor
 */
const _createSubcontractor = async (subcontractor) => {
  validateRequired(subcontractor.name, 'Subcontractor name')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const payload = {
    company_id: companyId,
    name: sanitizeInput(subcontractor.name),
    business_name: subcontractor.business_name ? sanitizeInput(subcontractor.business_name) : null,
    ein_or_ssn: subcontractor.ein_or_ssn || null,
    address_street: subcontractor.address_street ? sanitizeInput(subcontractor.address_street) : null,
    address_city: subcontractor.address_city ? sanitizeInput(subcontractor.address_city) : null,
    address_state: subcontractor.address_state || null,
    address_zip: subcontractor.address_zip || null,
    email: subcontractor.email || null,
    phone: subcontractor.phone || null,
    w9_on_file: !!subcontractor.w9_on_file,
    notes: subcontractor.notes ? sanitizeInput(subcontractor.notes) : null
  }

  const { data, error } = await supabase
    .from('subcontractors')
    .insert([payload])
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Update a subcontractor
 */
const _updateSubcontractor = async (id, subcontractorData) => {
  validateRequired(id, 'Subcontractor ID')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const sanitizedData = {
    ...subcontractorData,
    updated_at: new Date().toISOString()
  }

  if (sanitizedData.name) {
    sanitizedData.name = sanitizeInput(sanitizedData.name)
  }
  if (sanitizedData.business_name) {
    sanitizedData.business_name = sanitizeInput(sanitizedData.business_name)
  }
  if (sanitizedData.notes) {
    sanitizedData.notes = sanitizeInput(sanitizedData.notes)
  }
  if (sanitizedData.address_street) {
    sanitizedData.address_street = sanitizeInput(sanitizedData.address_street)
  }
  if (sanitizedData.address_city) {
    sanitizedData.address_city = sanitizeInput(sanitizedData.address_city)
  }

  const { data, error } = await supabase
    .from('subcontractors')
    .update(sanitizedData)
    .eq('id', id)
    .eq('company_id', companyId)
    .select()
    .single()

  if (error) throw error
  return data
}

/**
 * Delete a subcontractor
 */
const _deleteSubcontractor = async (id) => {
  validateRequired(id, 'Subcontractor ID')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const { error } = await supabase
    .from('subcontractors')
    .delete()
    .eq('id', id)
    .eq('company_id', companyId)

  if (error) throw error
  return { success: true }
}

/**
 * Get subcontractor payment summary for a given year
 * Sums all expenses linked to each subcontractor for that year
 */
const _getSubcontractorPaymentSummary = async (year) => {
  validateRequired(year, 'Year')

  const companyId = await getValidatedCompanyId()
  if (!companyId) return createSuccessResponse([])

  const startDate = `${year}-01-01`
  const endDate = `${year}-12-31`

  // Get all subcontractors
  const { data: subcontractors, error: subError } = await supabase
    .from('subcontractors')
    .select('*')
    .eq('company_id', companyId)
    .order('name', { ascending: true })

  if (subError) throw subError

  if (!subcontractors || subcontractors.length === 0) {
    return createSuccessResponse([])
  }

  const subIds = subcontractors.map(s => s.id)

  // Get all expenses linked to these subcontractors in the date range
  const { data: expenses, error: expError } = await supabase
    .from('expenses')
    .select('subcontractor_id, total_amount')
    .eq('company_id', companyId)
    .in('subcontractor_id', subIds)
    .gte('expense_date', startDate)
    .lte('expense_date', endDate)

  if (expError) throw expError

  // Aggregate expenses by subcontractor
  const paymentMap = {}
  ;(expenses || []).forEach(exp => {
    const subId = exp.subcontractor_id
    paymentMap[subId] = (paymentMap[subId] || 0) + parseFloat(exp.total_amount || 0)
  })

  // Build summary
  const summary = subcontractors.map(sub => ({
    ...sub,
    ytd_payments: paymentMap[sub.id] || 0,
    requires_1099: (paymentMap[sub.id] || 0) >= 600
  }))

  return createSuccessResponse(summary)
}

// Export wrapped functions
export const listSubcontractors = withErrorHandling(_listSubcontractors, 'list subcontractors', 'subcontractors')
export const createSubcontractor = withErrorHandling(_createSubcontractor, 'create subcontractor', 'subcontractor')
export const updateSubcontractor = withErrorHandling(_updateSubcontractor, 'update subcontractor', 'subcontractor')
export const deleteSubcontractor = withErrorHandling(_deleteSubcontractor, 'delete subcontractor', 'subcontractor')
export const getSubcontractorPaymentSummary = withErrorHandling(_getSubcontractorPaymentSummary, 'get subcontractor payment summary', 'subcontractor_summary')
